import React from "react";
import PropTypes from 'prop-types';
import { connect } from "react-redux";
import Video from "twilio-video";
import { alertError, openTwilioRoom, closeTwilioRoom, closeStream } from "actions/ui";

class TwilioVideoGoRoom extends React.Component {

	constructor(props) {
		super(props);

		this.state = {
			streamMessage: '',
			streamDetails: false,
			audioMuted: this.isAudioOff(),
			videoMuted: this.isVideoOff(),
			calling: false
		}
		this.ref = React.createRef();

		this.waitForAnswer = this.waitForAnswer.bind(this);
		this.handleParticipantConnect = this.handleParticipantConnect.bind(this);
		this.handleParticipantDisconnect = this.handleParticipantDisconnect.bind(this);
		this.handleDisconnect = this.handleDisconnect.bind(this);
		this.handleRoomDisconnect = this.handleRoomDisconnect.bind(this);
		this.disconnectRoomAndCloseStream = this.disconnectRoomAndCloseStream.bind(this);
		this.handleParticipantSubscribed = this.handleParticipantSubscribed.bind(this);
		this.handleParticipantUnsubscribed = this.handleParticipantUnsubscribed.bind(this);
		this.handleMuteAudio = this.handleMuteAudio.bind(this);
		this.handleUnmuteAudio = this.handleUnmuteAudio.bind(this);
		this.handleMuteVideo = this.handleMuteVideo.bind(this);
		this.handleUnmuteVideo = this.handleUnmuteVideo.bind(this);
		this.isAudioOff = this.isAudioOff.bind(this);
		this.isVideoOff = this.isVideoOff.bind(this);
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		if (prevProps.room !== this.props.room) {
			this.setState({
				audioMuted: this.isAudioOff(),
				videoMuted: this.isVideoOff(),
			});
		}
	}

	componentDidMount() {
		if (this.props.room) {
			const videoChatWindow = this.ref.current;
			//console.log(this.props.room);
			// re-attach all tracks:
			//this.props.room.localParticipant.tracks.forEach(publication => {
			//	videoChatWindow.appendChild(publication.track.attach());
			//});

			// re-add room subscriptions:
			this.props.room.on('participantConnected', this.handleParticipantConnect);
            this.props.room.on('participantDisconnected', this.handleParticipantDisconnect);
            this.props.room.on('disconnected', this.handleRoomDisconnect);

			// loop through all remote participants and re-attach their tracks:
			this.props.room.participants.forEach(participant => {
				participant.on('trackSubscribed', this.handleParticipantSubscribed);
                participant.on('trackUnsubscribed', this.handleParticipantUnsubscribed);
				participant.tracks.forEach(publication => {
					if (publication.isSubscribed) {
						videoChatWindow.appendChild(publication.track.attach());
						console.log('re-attach');
					}
				});
			});
		} else {
			this.connectToRoom();
		}
	}

	componentWillUnmount() {
		// release any timer to avoid memory leaks:
		this.clearAllTimers();

		// detach the track because DOM is being destroyed
		if (this.props.room) {
			// remove all subscriptions related to the room:
			this.props.room.removeListener('participantConnected', this.handleParticipantConnect);
            this.props.room.removeListener('participantDisconnected', this.handleParticipantDisconnect);
            this.props.room.removeListener('disconnected', this.handleRoomDisconnect);

			// loop through all remote participants and detach the tracks:
			this.props.room.participants.forEach(participant => {
			    participant.removeListener('trackSubscribed', this.handleParticipantSubscribed);
                participant.removeListener('trackUnsubscribed', this.handleParticipantUnsubscribed);
				participant.tracks.forEach(publication => {
					if (publication.isSubscribed) {
						const attachedElements = publication.track.detach();
						attachedElements.forEach(element => element.remove());
					}
				});
			});
		}
	}

	handleParticipantConnect(participant) {
		const videoChatWindow = this.ref.current;

		this.setState({ calling: false });
		this.setStreamMessage(undefined);
		this.clearAllTimers();

        participant.tracks.forEach(publication => {
            if (publication.isSubscribed) {
                const track = publication.track;
                videoChatWindow.track(track.attach());
            }
        });

        participant.on('trackSubscribed', this.handleParticipantSubscribed);
        participant.on('trackUnsubscribed', this.handleParticipantUnsubscribed);
	}

	handleParticipantSubscribed(track) {
		const videoChatWindow = this.ref.current;
		videoChatWindow.appendChild(track.attach());
		this.setState({ streamDetails: false });
	}

	handleParticipantUnsubscribed(track) {
		track.detach().forEach((element) => element.remove());
		this.setState({ streamDetails: true });
	}

	handleParticipantDisconnect() {
		this.disconnectRoomAndCloseStream(this.props.i18n.callWasEndedRemote);
	}

	handleRoomDisconnect(room, error) {
		this.setState({ calling: false });

		if (error) {
			this.props.alertError('Twilio room disconnect error: ' + error.message);
		} else {
			// this is required if video mute action was run:
			room.localParticipant.videoTracks.forEach(publication => {
				publication.track.stop();
	            publication.unpublish();
			});
		}
		this.props.closeTwilioRoom();
	}

	connectToRoom() {
        const { connect, createLocalVideoTrack } = Video;

        let connectOption = { name: this.props.stream.room };
        let token = this.props.stream.token;

        this.setStreamMessage(this.props.i18n.calling);
        this.setState({ calling: true });
        connect( token, connectOption).then(room => {
        	this.props.openTwilioRoom(room);

        	// waiting for picking up the call:
        	this.waitForAnswer();

			//const videoChatWindow = this.ref.current;
			//let localVideoTrack = null;
            //createLocalVideoTrack().then(track => {
            //    videoChatWindow.appendChild(track.attach());
            //    localVideoTrack = track;
            //});

	        room.participants.forEach(this.handleParticipantConnect); // handle current participants:
            room.on('participantConnected', this.handleParticipantConnect); // handle future participants
            room.on('participantDisconnected', this.handleParticipantDisconnect);
			room.on('disconnected', this.handleRoomDisconnect);
        }, error => {
        	this.setState({ calling: false });
        	this.props.alertError('Unable to connect: ' + error.message);
        });
    };

	waitForAnswer() {
		this.connectTimer = setTimeout(this.disconnectRoomAndCloseStream, this.props.configuration.interface.streams.video.calls.callingTimeout * 1000, this.props.i18n.callWasNotAnswered);
	}

	handleDisconnect() {
		this.disconnectRoomAndCloseStream(this.props.i18n.callWasEnded);
	}

	handleMuteAudio() {
		if (!this.props.room) {
			return;
		}

		this.props.room.localParticipant.audioTracks.forEach(publication => {
			publication.track.disable();
		});
		this.setState({ audioMuted: true });
	}

	handleUnmuteAudio() {
		if (!this.props.room) {
			return;
		}

		this.props.room.localParticipant.audioTracks.forEach(publication => {
			publication.track.enable();
		});
		this.setState({ audioMuted: false });
	}

	handleMuteVideo() {
		if (!this.props.room) {
			return;
		}
		this.props.room.localParticipant.videoTracks.forEach(publication => {
			publication.track.stop();
            publication.unpublish();
		});
		this.setState({ videoMuted: true });
	}

	handleUnmuteVideo() {
		if (!this.props.room) {
			return;
		}

		const { createLocalVideoTrack } = Video;

		createLocalVideoTrack().then(localVideoTrack => {
			return this.props.room.localParticipant.publishTrack(localVideoTrack);
		}).then(publication => {
			this.setState({ videoMuted: false });
		});
	}

	isAudioOff() {
		if (!this.props.room) {
			return undefined;
		}
		const audioTracks = [...this.props.room.localParticipant.audioTracks].map(([key, value]) => value);

		return audioTracks.length > 0 && audioTracks.filter( publication => !publication.track.isEnabled ).length === audioTracks.length;
	}

	isVideoOff() {
		if (!this.props.room) {
			return undefined;
		}
		const videoTracks = [...this.props.room.localParticipant.videoTracks].map(([key, value]) => value);

		return videoTracks.length > 0 && videoTracks.filter( publication => publication.track.isStopped ).length === videoTracks.length;
	}

	setStreamMessage(message) {
		this.setState({ streamMessage: message });
	}

	clearAllTimers() {
		if (this.disconnectTimer) {
			clearTimeout(this.disconnectTimer);
			delete this.disconnectTimer;
		}
		if (this.connectTimer) {
			clearTimeout(this.connectTimer);
			delete this.connectTimer;
		}
	}

	disconnectRoomAndCloseStream(message) {
		if (this.props.room) {
			setTimeout(() => { this.props.room.disconnect(); }, 10); // avoid problems with Twilio when room.disconnect() is run directly in handleParticipantDisconnect
		}
		this.setStreamMessage(message);
		this.clearAllTimers();
		this.disconnectTimer = setTimeout(() => {
			this.props.closeStream(this.props.stream.id);
		}, 4000)
	}

	render() {
		return(
			<div className="wcStreamContent">
				<div ref={ this.ref } />
				{ this.state.streamMessage && <div className="wcStreamInfo">{ this.state.streamMessage }</div> }
				{ !this.state.streamMessage && this.state.streamDetails && <div className="wcStreamDetails">{ this.props.streamInfo }</div> }

				<div className="wcStreamToolbar">
					{ this.state.audioMuted === false && <button className="wcStreamButton wcFunctional wcMuteAudio" title={ this.props.i18n.audioMute } onClick={ this.handleMuteAudio } /> }
					{ this.state.audioMuted && <button className="wcStreamButton wcFunctional wcUnmuteAudio" title={ this.props.i18n.audioUnmute } onClick={ this.handleUnmuteAudio } /> }

					{ this.state.videoMuted === false && <button className="wcStreamButton wcFunctional wcMuteVideo" title={ this.props.i18n.videoMute } onClick={ this.handleMuteVideo } /> }
					{ this.state.videoMuted && <button className="wcStreamButton wcFunctional wcUnmuteVideo" title={ this.props.i18n.videoUnmute } onClick={ this.handleUnmuteVideo } /> }

					{ this.props.room && !this.props.maximized && <button className="wcStreamButton wcFunctional wcMaximizeVideo" title={ this.props.i18n.maximize } onClick={ this.props.onMaximize } /> }
					{ this.props.room && this.props.maximized && <button className="wcStreamButton wcFunctional wcMinimizeVideo" title={ this.props.i18n.minimize } onClick={ this.props.onMinimize } /> }

					{ this.props.room && <button className="wcStreamButton wcFunctional wcStreamEnd" title={ this.props.i18n.endCall } onClick={ this.handleDisconnect } /> }
				</div>

				{ this.props.configuration.interface.streams.video.calls.callingSound && this.state.calling &&
					<audio loop autoPlay preload="auto">
						<source src={ this.props.configuration.baseDir + 'assets/sounds/calling01.mp3' } type="audio/ogg" />
					</audio>
				}
			</div>
		)
	}

}

TwilioVideoGoRoom.propTypes = {
	stream: PropTypes.object.isRequired,
	streamInfo: PropTypes.object.isRequired,
	maximized: PropTypes.bool,
	onMaximize: PropTypes.func.isRequired,
	onMinimize: PropTypes.func.isRequired
};

export default connect(
	(state, ownProps) => ({
		configuration: state.configuration,
		i18n: state.application.i18n,
		room: state.ui.twilio.room && state.ui.twilio.room.name === ownProps.stream.room ? state.ui.twilio.room : undefined,
	}), { alertError, openTwilioRoom, closeTwilioRoom, closeStream }
)(TwilioVideoGoRoom);